import { Form, Input, Radio } from "antd";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomActionButtons,
  CustomInput,
  CustomModal,
  CustomSelect,
} from "sipli-fleet-core/components";
import {
  useMutationCreateUnitBrand,
  useMutationCreateUnitModel,
  useMutationUpdateUnitBrand,
  useMutationUpdateUnitModel,
  useQueryGetUnitBrandLookup,
  useQueryGetUnitTypeLookup,
} from "src/queries/administrationSetup.queries";
import type {
  UnitBrandPayload,
  UnitBrandRecord,
  UnitModelPayload,
  UnitModelRecord,
} from "src/services/administrationSetupService";
import Notification from "src/util/libs/notification";
import { getLookupItems, isActive } from "../setupUtils";

type SetupKind = "brand" | "model";
type SetupRecord = UnitBrandRecord | UnitModelRecord;

interface UnitBrandAddEditProps {
  open: boolean;
  record?: SetupRecord | null;
  onClose: () => void;
}

interface UnitBrandFormValues {
  name: string;
  active: boolean;
  description?: string;
}

interface UnitModelFormValues extends UnitBrandFormValues {
  makeId?: string | number;
  code: string;
  commCode: string;
  unitTypeId?: string | number;
}

type UnitSetupFormValues = UnitBrandFormValues & Partial<UnitModelFormValues>;

const getModelRecord = (record?: SetupRecord | null) =>
  record as UnitModelRecord | null | undefined;

const getSetupKind = (): SetupKind => "brand";

const UnitBrandAddEdit = ({
  open,
  record,
  onClose,
}: UnitBrandAddEditProps) => {
  const kind = getSetupKind();
  const { t } = useTranslation();
  const [form] = Form.useForm<UnitSetupFormValues>();
  const isEditing = Boolean(record);
  const isModel = kind === "model";
  const createBrandMutation = useMutationCreateUnitBrand();
  const updateBrandMutation = useMutationUpdateUnitBrand();
  const createModelMutation = useMutationCreateUnitModel();
  const updateModelMutation = useMutationUpdateUnitModel();
  const brandLookupQuery = useQueryGetUnitBrandLookup(open && isModel && !isEditing);
  const unitTypeLookupQuery = useQueryGetUnitTypeLookup(open && isModel);
  const modelRecord = getModelRecord(record);

  const brandOptions = useMemo(
    () =>
      getLookupItems(brandLookupQuery.data?.data).map((item) => ({
        value: item.id,
        label: item.name ?? item.code ?? String(item.id),
      })),
    [brandLookupQuery.data?.data],
  );

  const unitTypeOptions = useMemo(
    () =>
      getLookupItems(unitTypeLookupQuery.data?.data).map((item) => ({
        value: item.id,
        label: item.name ?? item.code ?? String(item.id),
      })),
    [unitTypeLookupQuery.data?.data],
  );

  useEffect(() => {
    if (!open) return;

    if (isModel) {
      form.setFieldsValue({
        name: modelRecord?.name ?? "",
        active: record ? isActive(record.active) : true,
        description: modelRecord?.description ?? "",
        makeId: modelRecord?.make?.id,
        code: modelRecord?.code ?? "",
        commCode: modelRecord?.commCode ?? "",
        unitTypeId: modelRecord?.unitType?.id,
      });
      return;
    }

    form.setFieldsValue({
      name: record?.name ?? "",
      active: record ? isActive(record.active) : true,
      description: record?.description ?? "",
    });
  }, [form, isModel, modelRecord, open, record]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();

    try {
      if (isModel) {
        const payload: UnitModelPayload = {
          name: values.name.trim(),
          makeId: values.makeId!,
          code: values.code?.trim() ?? "",
          commCode: values.commCode?.trim() ?? "",
          unitTypeId: values.unitTypeId!,
          description: values.description?.trim() ?? "",
          active: values.active,
        };

        if (record) {
          await updateModelMutation.mutateAsync({ id: record.id, payload });
        } else {
          await createModelMutation.mutateAsync(payload);
        }
      } else {
        const payload: UnitBrandPayload = {
          name: values.name.trim(),
          description: values.description?.trim() ?? "",
          active: values.active,
        };

        if (record) {
          await updateBrandMutation.mutateAsync({ id: record.id, payload });
        } else {
          await createBrandMutation.mutateAsync(payload);
        }
      }

      Notification.success({
        message: t(
          isModel
            ? isEditing
              ? "unit_model_updated"
              : "unit_model_added"
            : isEditing
              ? "unit_make_updated"
              : "unit_make_added",
        ),
      });
      handleClose();
    } catch (error: unknown) {
      const responseMessage =
        error instanceof Error
          ? (error as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : undefined;

      if (!responseMessage) {
        Notification.error({
          message: t("error"),
          description: t("setup_save_failed"),
        });
      }
    }
  };

  const isLoading =
    createBrandMutation.isPending ||
    updateBrandMutation.isPending ||
    createModelMutation.isPending ||
    updateModelMutation.isPending;

  return (
    <CustomModal
      open={open}
      title={t(
        isModel
          ? isEditing
            ? "edit_unit_model"
            : "add_unit_model"
          : isEditing
            ? "edit_unit_make"
            : "add_unit_make",
      )}
      onCancel={handleClose}
      destroyOnHidden
      width={isModel ? 720 : 560}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        className="grid grid-cols-1 pt-4 gap-x-4 sm:grid-cols-2"
      >
        <Form.Item
          name="name"
          label={t(isModel ? "name" : "unit_make_name")}
          rules={[
            { required: true, whitespace: true, message: t("name_required") },
            {
              max: isModel ? 64 : undefined,
              message: t("name_max_length", { count: 64 }),
            },
          ].filter((rule) => rule.max !== undefined || "required" in rule)}
          className="mb-3!"
        >
          <CustomInput placeholder={t("enter_name")} maxLength={isModel ? 64 : 150} />
        </Form.Item>

        {isModel && (
          <Form.Item
            name="makeId"
            label={t("unit_make")}
            rules={[{ required: true, message: t("unit_make_required") }]}
            className="mb-3!"
          >
            <CustomSelect
              options={
                isEditing && modelRecord?.make?.id
                  ? [
                      {
                        value: modelRecord.make.id,
                        label:
                          modelRecord.make.name ??
                          modelRecord.make.code ??
                          String(modelRecord.make.id),
                      },
                    ]
                  : brandOptions
              }
              placeholder={t("select_unit_make")}
              loading={brandLookupQuery.isLoading}
              showSearch
              disabled={isEditing || brandLookupQuery.isLoading}
              optionFilterProp="label"
            />
          </Form.Item>
        )}

        {isModel && (
          <>
            <Form.Item
              name="code"
              label={t("code")}
              rules={[
                { required: true, whitespace: true, message: t("code_required") },
                { max: 32, message: t("code_max_length", { count: 32 }) },
              ]}
              className="mb-3!"
            >
              <CustomInput placeholder={t("enter_code")} maxLength={32} />
            </Form.Item>

            <Form.Item
              name="commCode"
              label={t("commercial_code")}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: t("commercial_code_required"),
                },
                {
                  max: 32,
                  message: t("commercial_code_max_length", { count: 32 }),
                },
              ]}
              className="mb-3!"
            >
              <CustomInput
                placeholder={t("enter_commercial_code")}
                maxLength={32}
              />
            </Form.Item>

            <Form.Item
              name="unitTypeId"
              label={t("unit_type")}
              rules={[{ required: true, message: t("unit_type_required") }]}
              className="mb-3!"
            >
              <CustomSelect
                options={unitTypeOptions}
                placeholder={t("select_unit_type")}
                loading={unitTypeLookupQuery.isLoading}
                showSearch
                disabled={unitTypeLookupQuery.isLoading}
                optionFilterProp="label"
              />
            </Form.Item>
          </>
        )}

        <Form.Item name="active" label={t("status")} className="mb-3!">
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value>{t("active")}</Radio.Button>
            <Radio.Button value={false}>{t("inactive")}</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="description"
          label={t("description")}
          rules={[
            {
              max: 255,
              message: t("description_too_long"),
            },
          ]}
          className="sm:col-span-2 mb-3!"
        >
          <Input.TextArea
            rows={4}
            placeholder={t("enter_description")}
            maxLength={255}
            className="px-2 py-4 custom-input no-resize"
          />
        </Form.Item>

        <Form.Item className="sm:col-span-2 mb-0!">
          <CustomActionButtons
            onCancel={handleClose}
            onOk={handleSubmit}
            loading={isLoading}
            okProps={{ htmlType: "button" }}
            cancelBtnLabel="cancel"
            okBtnLabel={isEditing ? "update" : "add"}
          />
        </Form.Item>
      </Form>
    </CustomModal>
  );
};

export default UnitBrandAddEdit;
